import { ArrowUpRight, FileDown } from "lucide-react";
import { fileToUrl } from "@/content/ContentContext";

export default function Configurations({ content }) {
  const c = content || {};
  const units = c.units || [];
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="configurations"
      data-testid="configurations-section"
      className="relative py-24 md:py-36 lg:py-44 bg-[var(--bone-2)]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16 md:mb-24">
          <div className="lg:col-span-5">
            <p className="overline mb-6">{c.overline}</p>
            <h2
              data-testid="configurations-title"
              className="font-serif-display text-3xl md:text-4xl lg:text-5xl leading-[1.15] tracking-tight font-medium"
            >
              {c.title}
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 self-end">
            <p className="text-[var(--ink-soft)] text-base md:text-lg leading-relaxed">
              {c.intro}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          <article
            data-testid="config-card-residential"
            className="reveal group relative md:col-span-7 row-span-2 overflow-hidden bg-[var(--bone)] h-[480px] md:h-[640px]"
          >
            <img
              src={fileToUrl(c.residential_image_url)}
              alt="Residential interior"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)]/85 via-[var(--ink)]/15 to-transparent" />
            <div className="absolute inset-0 p-8 md:p-12 flex flex-col justify-between text-[var(--bone)]">
              <div className="flex items-start justify-between">
                <span className="overline text-[var(--bone-2)]">Residential</span>
                <ArrowUpRight
                  size={22}
                  className="opacity-80 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
                />
              </div>
              <div>
                <h3 className="font-serif-display text-3xl md:text-5xl leading-tight max-w-md">
                  {c.residential_title}
                </h3>
                <p className="mt-4 max-w-md text-[var(--bone-2)] text-sm md:text-base leading-relaxed">
                  {c.residential_body}
                </p>
              </div>
            </div>
          </article>

          <article
            data-testid="config-card-commercial"
            className="reveal group relative md:col-span-5 overflow-hidden bg-[var(--bone)] h-[320px] md:h-[306px]"
          >
            <img
              src={fileToUrl(c.commercial_image_url)}
              alt="Commercial interior"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)]/80 via-[var(--ink)]/10 to-transparent" />
            <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-between text-[var(--bone)]">
              <span className="overline text-[var(--bone-2)]">Commercial</span>
              <div>
                <h3 className="font-serif-display text-2xl md:text-3xl">
                  {c.commercial_title}
                </h3>
                <p className="mt-2 text-[var(--bone-2)] text-sm leading-relaxed max-w-sm">
                  {c.commercial_body}
                </p>
              </div>
            </div>
          </article>

          <div
            data-testid="config-card-list"
            className="reveal md:col-span-5 bg-[var(--bone)] p-8 md:p-10"
          >
            <p className="overline mb-6">{units.length} Configurations</p>
            <ul className="divide-y divide-[var(--line)]">
              {units.map((u) => (
                <li key={u.code} className="py-5 flex items-start gap-5">
                  <span className="font-serif-display text-2xl text-[var(--terracotta)] w-10 shrink-0">
                    {u.code}
                  </span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between gap-4">
                      <h4 className="font-serif-display text-lg md:text-xl">
                        {u.title}
                      </h4>
                      <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--ink-soft)] whitespace-nowrap">
                        {u.tag}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-[var(--ink-soft)] leading-relaxed">
                      {u.desc}
                    </p>
                    {u.layout_url && (
                      <a
                        href={fileToUrl(u.layout_url)}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`unit-${u.code}-layout-link`}
                        className="inline-flex items-center gap-2 mt-3 text-xs uppercase tracking-[0.2em] font-bold text-[var(--terracotta)] hover:text-[var(--terracotta-dark)]"
                      >
                        <FileDown size={14} />
                        View layout
                      </a>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            <button
              data-testid="config-register-btn"
              onClick={() => scrollTo("register")}
              className="btn-primary mt-8"
            >
              Enquire for floor plans
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
